/**
 * Turns a connector target transaction outcome into the English failure notice
 * the Connectors step shows. Pure: it renders nothing and touches no store.
 */

import type {
  ConnectorTargetOutcome,
  ConnectorTargetTransactionResult,
} from '../../../shared/lib';

export interface ConnectorParameterFailure {
  summary: string;
  details: string[];
}

export const missingProjectIdentityFailure: ConnectorParameterFailure = {
  summary: 'PipeEX cannot identify the active Revit project, so no connector parameters were changed.',
  details: ['Open or refresh the intended Revit model, keep it active, and select Continue again.'],
};

export const changedProjectIdentityFailure: ConnectorParameterFailure = {
  summary: 'The active Revit project changed before connector parameter setup finished.',
  details: ['Activate the intended Revit model, review the selected parameter target, and select Continue again.'],
};

interface ConnectorParameterHostResult {
  error?: string;
  failedParameters?: string[];
  conflictingParameters?: string[];
  failureMessages?: string[];
}

/** Names the host reported for individual parameters, in the order it sent them. */
const collectNamedDetails = (result: ConnectorParameterHostResult): string[] =>
  [...(result.failedParameters ?? []), ...(result.conflictingParameters ?? [])]
    .map((detail) => detail.trim())
    .filter(Boolean);

/** Messages that are not already spelled out by a named parameter detail. */
const collectUnnamedDetails = (
  result: ConnectorParameterHostResult,
  namedDetails: string[]
): string[] =>
  (result.failureMessages ?? [])
    .map((detail) => detail.trim())
    .filter(
      (detail) =>
        Boolean(detail) &&
        !namedDetails.some((namedDetail) => namedDetail.endsWith(`: ${detail}`))
    );

export function buildConnectorParameterFailure(
  result: ConnectorParameterHostResult
): ConnectorParameterFailure {
  const namedDetails = collectNamedDetails(result);

  return {
    summary: result.error?.trim() || 'Revit could not create or repair every required connector parameter.',
    details: Array.from(new Set([...namedDetails, ...collectUnnamedDetails(result, namedDetails)])),
  };
}

/** Block reasons the page already words in terms of the project the user sees. */
const blockedFailures: Partial<
  Record<Extract<ConnectorTargetOutcome, { kind: 'blocked' }>['reason'], ConnectorParameterFailure>
> = {
  'unknown-project': missingProjectIdentityFailure,
  'document-changed': changedProjectIdentityFailure,
};

/**
 * Details belong to the mutation, so they are only meaningful once one ran.
 * The transaction message stays the summary: it explains what the model holds.
 */
const failureFromMutation = (
  message: string,
  applyResult: ConnectorTargetTransactionResult['applyResult']
): ConnectorParameterFailure => ({
  summary: message,
  details: applyResult ? buildConnectorParameterFailure(applyResult).details : [],
});

export function describeConnectorTargetFailure(
  result: ConnectorTargetTransactionResult
): ConnectorParameterFailure | null {
  const { outcome } = result;

  switch (outcome.kind) {
    case 'applied':
    case 'unchanged':
      return null;
    case 'blocked':
      return blockedFailures[outcome.reason] ?? { summary: outcome.message, details: [] };
    default:
      return failureFromMutation(outcome.message, result.applyResult);
  }
}
